const encoder = new TextEncoder();

// base64url encode (RFC 4648 §5): no padding, + → -, / → _
export const base64urlEncode = (data: Uint8Array): string => {
  const binString = String.fromCharCode(...data);
  return btoa(binString).replace(/\+/g, '-').replace(/\//g, '_').replace(/[=]/g, '');
};

export const base64urlEncodeJson = (obj: object): string => {
  const bytes = encoder.encode(JSON.stringify(obj));
  return base64urlEncode(bytes);
};

const sha256base64url = async (input: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(input));
  return base64urlEncode(new Uint8Array(digest));
};

/**
 * Normalize htu per RFC 9449 §4.2:
 * - scheme + authority + path
 * - lowercase scheme and host
 * - no query string
 * - no fragment
 */
export const normalizeHtu = (url: string): string => {
  const parsed = new URL(url);
  return `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
};

/**
 * JWK Thumbprint (RFC 7638) for EC P-256 public key
 * Members MUST be in lexicographic order: crv < kty < x < y
 */
export const calculateJkt = (publicJwk: JsonWebKey): Promise<string> =>
  sha256base64url(
    JSON.stringify({ crv: publicJwk.crv!, kty: publicJwk.kty!, x: publicJwk.x!, y: publicJwk.y! }),
  );

/**
 * Access Token Hash (RFC 9449 §7.1)
 * ath = base64url(SHA-256(ASCII(access_token)))
 */
export const createAth = (accessToken: string): Promise<string> => sha256base64url(accessToken);

/** Strip "Bearer " prefix if present */
export const stripBearer = (authHeader: string): string => authHeader.replace(/^Bearer\s+/i, '');

/**
 * Extract cnf.jkt from a decoded access token payload.
 * Returns null if the token is not DPoP-bound.
 */
export const extractJktFromToken = (accessToken: string): string | null => {
  try {
    const parts = accessToken.split('.');
    if (parts.length !== 3) return null;

    const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson) as { cnf?: { jkt?: string } };

    return payload.cnf?.jkt ?? null;
  } catch {
    return null;
  }
};
